import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

import CommunityStatsBar from '@/components/atomic/molecules/CommunityStatsBar'
import { NONMEMBER_ID } from '@/common/constants'
import { postAmplitude } from '@/core/lib'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { generateTagId } from '@/utils/test-automation'
import { DocumentByDoctor } from '@/features/community/api/document/community-document.types'
import { DoctorDetailAmplitudeType } from '@/features/doctors/[doctorId]/index/helpers/types/event-types'

interface Props {
  document: DocumentByDoctor
  documentIndex: number
  userId: number
  commonAmplitudeProps: DoctorDetailAmplitudeType
  onClickLoginAndWatch: (index: 'comment' | 'document', count: number) => void
}

export const DocumentCard = ({
  document,
  documentIndex,
  userId,
  commonAmplitudeProps,
  onClickLoginAndWatch,
}: Props) => {
  const { t } = useTranslation()
  const MAXIMUM_COMMUNITY_POST_CHARACTER_COUNT = 104
  const router = useRouter()

  return (
    <Link
      aria-label="post card"
      href={userId === NONMEMBER_ID ? '#' : `/community/${document.communityId}`}
      key={document.communityId}
      onClick={() => {
        if (userId !== NONMEMBER_ID) {
          router.push(`/community/${document.communityId}`)
        }

        postAmplitude('click_activity_contents_in_doctor_detail', {
          index: 'document',
          seq: documentIndex,
          ...commonAmplitudeProps,
        })
      }}
      className="relative flex-none w-[220px] min-h-[220px] flex flex-col p-4 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 no-underline hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
    >
      {userId === NONMEMBER_ID ? (
        <>
          <img
            src="/images/dummy_community_activity.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover rounded-xl opacity-30"
          />
          <button
            id={generateTagId('doctor', 'page', 'btn', 'login', 'post')}
            aria-label="login button"
            onClick={(e) => {
              e.stopPropagation()
              sendAirbridgeEvent(AirbridgeEvents.SIGN_UP_BUTTON_CLICK_IN_WEB, {})
              onClickLoginAndWatch('document', document.commentCount)
            }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-4 py-2 text-orange-500 bg-white text-sm font-medium border border-orange-500 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
          >
            {t('pages.doctorDetail.loginAndWatch')}
          </button>
        </>
      ) : (
        <>
          <div className="flex-1">
            <div
              className="text-sm leading-relaxed text-gray-800"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 9,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxHeight: '12.6em',
              }}
            >
              {document.contents}
              {document.contents.length > MAXIMUM_COMMUNITY_POST_CHARACTER_COUNT && (
                <span className="text-gray-500 font-medium ml-1">...{t('pages.doctorDetail.seeMore')}</span>
              )}
            </div>
          </div>
          <div className="mt-auto pt-3 border-t border-gray-100">
            <CommunityStatsBar
              helpfulCount={document.helpfulCount}
              commentCount={document.commentCount}
              viewCount={document.viewCount}
            />
          </div>
        </>
      )}
    </Link>
  )
}
